import {
  ApiProjectSettingKey,
  ApiSync,
  ApiSyncStatus,
  ApiSyncType,
} from "@incendium/api";
import {
  AlertTitle,
  Alert,
  Portal,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { ClickableTooltip } from "Components/ClickableTooltip";
import { StyledHelp } from "Components/ClickableTooltip/StyledHelp";
import { StyledFlexBox } from "Components/UI/StylesFlexBox";
import { PageSubAction, PageTitle } from "consts";
import CampaignAnalysisExplorer from "../components/CampaignAnalysisExplorer";
import { motion } from "motion/react";
import { useSelectedProject } from "Hooks";
import { useEffect, useMemo, useState } from "react";
import useAppendSearchState from "Hooks/useAppendSearchState";
import { useExternalSyncs } from "Hooks/useExternalSyncs";
import Loading from "Components/Loading/Loading";

const syncTypeName: Partial<Record<ApiSyncType, string>> = {
  [ApiSyncType.FACEBOOK_ADS]: "Facebook Ads",
  [ApiSyncType.GOOGLE_ADS]: "Google Ads",
  [ApiSyncType.BING_ADS]: "Bing Ads",
};

function CampaignAnalysis() {
  const [exploreMode, setExploreMode] = useAppendSearchState("ca-m", true);
  const [useMatchType, setUseMatchType] = useState(false);
  const { projectSettings, selectedProject } = useSelectedProject();
  const { externalSyncs, fetched: syncsFetched } = useExternalSyncs();
  const [errorSyncs, setErrorSyncs] = useState<ApiSync[]>([]);

  useEffect(() => {
    if (!externalSyncs) {
      return;
    }
    const errorSyncs = externalSyncs.filter(
      (sync) => sync.status === ApiSyncStatus.ERROR,
    );
    setErrorSyncs(errorSyncs);
  }, [externalSyncs]);

  const hasMatchType = useMemo(() => {
    if (!projectSettings) {
      return false;
    }
    return (
      projectSettings[ApiProjectSettingKey.BING_ENABLE_MATCH_TYPE] === "true" ||
      projectSettings[ApiProjectSettingKey.GOOGLE_ENABLE_MATCH_TYPE] === "true"
    );
  }, [projectSettings]);

  if (!syncsFetched) {
    return <Loading />;
  }

  return (
    <>
      <Portal container={() => document.getElementById(PageTitle)}>
        <Stack
          component={motion.div}
          initial={{ x: -50, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 1 }}
          direction={"row"}
          spacing={2}
          mt={1}
        >
          {hasMatchType && (
            <StyledFlexBox>
              <Stack direction={"row"} alignItems="center" mr={4}>
                <Typography color={"secondary"}>Term</Typography>
                <Switch
                  checked={useMatchType}
                  onChange={(e, checked) => setUseMatchType(checked)}
                />
                <Typography color={"primary"}>Term With Matchtype</Typography>
              </Stack>
              <ClickableTooltip
                placement="bottom"
                width={335}
                text={
                  <>
                    <Typography variant="body2" color={"inherit"} mb={1}>
                      <strong>Select Term With Matchtype</strong> to append
                      matchtype to you terms, <i>note</i> only works for
                      providers where you have turned on this feature in the
                      integration config.
                    </Typography>
                  </>
                }
                icon={<StyledHelp />}
              />
            </StyledFlexBox>
          )}
          <StyledFlexBox compact>
            <Stack direction={"row"} alignItems="center" mr={4}>
              <Typography color={"secondary"}>Analyze</Typography>
              <Switch
                checked={Boolean(exploreMode)}
                onChange={(e, checked) => setExploreMode(checked)}
              />
              <Typography color={"primary"}>Explore</Typography>
            </Stack>
            <ClickableTooltip
              placement="bottom"
              width={335}
              text={
                <>
                  <Typography variant="body2" color={"inherit"} mb={1}>
                    <strong>Analyze</strong>: Gain an overview with a standard
                    table showcasing the full picture of your campaigns.
                  </Typography>
                  <Typography variant="body2" color={"inherit"}>
                    <strong>Explore</strong>: Dive deeper by drilling down into
                    your campaigns. (Handy tip: Hold Shift while selecting
                    options to choose multiple items.)"
                  </Typography>
                </>
              }
              icon={<StyledHelp />}
            />
          </StyledFlexBox>
        </Stack>
      </Portal>

      {errorSyncs.length > 0 && (
        <Portal container={() => document.getElementById(PageSubAction)}>
          <Alert severity="error" sx={{ width: 450 }}>
            <AlertTitle>There are errors with the following syncs:</AlertTitle>
            {errorSyncs
              .filter((sync) => sync.type)
              .map((sync) => (
                <Typography variant="body2" color={"inherit"} key={sync.id}>
                  {syncTypeName[sync.type as ApiSyncType]} - {sync.error}
                </Typography>
              ))}
          </Alert>
        </Portal>
      )}

      <CampaignAnalysisExplorer
        mode={exploreMode ? "explore" : "analyze"}
        useMatchType={useMatchType}
        projectId={selectedProject?.id as number}
      />
    </>
  );
}

export default CampaignAnalysis;
